<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { TableField, Tabs } from "../../../../components/table/table";
import { onMounted, ref } from "vue";
import { isEmpty } from "../../../../utils/CommonFunctions";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import AmountInput from "../../../../components/ui/form/AmountInput.vue";
import SearchSelectMultiItems from "../../../../components/ui/form/SearchSelectMultiItems.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import ItemPricesListView from "./itemprices/ItemPricesListView.vue";
import ItemPackingPricesListView from "./itempackingprices/ItemPackingPricesListView.vue";
import ItemQuantityPricesListView from "./itemquantityprices/ItemQuantityPricesListView.vue";
import InventoryListView from "./inventory/InventoryListView.vue";
import ItemVariantsList from "./variants/ItemVariantsList.vue";
import ItemVariantInventory from "./inventory/ItemVariantInventory.vue";
import BatchListView from "./batches/BatchListView.vue";
import SerialListView from "./serials/SerialListView.vue";
import BarodesListView from "./barcodes/BarodesListView.vue";
import PhotosListView from "./photos/PhotosListView.vue";
import VariantList from "./variants/VariantList.vue";
import GridInput from "../../../../components/ui/form/GridInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  listCreateCode?: string;
  lists?: any;
  ignorePermission?: boolean;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const trackedByList = [
  { id: "Quantity", name: "Quantity" },  
  { id: "Serial", name: "Serial" },
  { id: "Batch", name: "Batch" },
  { id: "Service", name: "Service" },
  { id: "Expense", name: "Expense" },
];

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  id: props.data?.id || "",
  name: props.data?.name || props.listCreateName || "",
  photo: props.data?.photo || "",
  code: props.data?.code || props.listCreateCode || "",
  sales_name: props.data?.sales_name || "",
  item_types_id: props.data?.item_types_id || "",
  brands_id: props.data?.brands_id || "",
  short_description: props.data?.short_description || "",
  description: props.data?.description || "",
  inventory_uom_id: props.data?.inventory_uom_id || "",
  inventory_uom_qty: props.data?.inventory_uom_qty || 1,
  purchase_uom_id: props.data?.purchase_uom_id || "",
  purchase_uom_qty: props.data?.purchase_uom_qty || 1,
  sales_uom_id: props.data?.sales_uom_id || "",
  sales_uom_qty: props.data?.sales_uom_qty || 1,
  is_package: props.data?.is_package || false,
  flexi_price: props.data?.flexi_price || false,
  ignore_discount: props.data?.ignore_discount || false,
  use_package_price: props.data?.use_package_price || false,
  restrict_outlets: props.data?.restrict_outlets || false,
  outlets:
    props.data?.outlets?.variants?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  tracked_by: props.data?.tracked_by || "",
  is_expiry: props.data?.is_expiry || false,
  tax_details_id: props.data?.tax_details_id || "",
  has_variants: props.data?.has_variants || false,
  weight: props.data?.weight || "",
  volume: props.data?.volume || "",
  internal_notes: props.data?.internal_notes || "",
  optional_products:
    props.data?.optional_products?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  accessory_products:
    props.data?.accessory_products?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  alternative_products:
    props.data?.alternative_products?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  out_of_stock_message: props.data?.out_of_stock_message || "",
  item_groups:
    props.data?.item_groups?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  item_categories:
    props.data?.item_categories?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  to_weigh: props.data?.to_weigh || false,
  allow_pre_order: props.data?.allow_pre_order || true,
  income_accounts_id: props.data?.income_accounts_id || "",
  expense_accounts_id: props.data?.expense_accounts_id || "",
  active: props.data?.active || true,
  uid: props.data?.uid || "",
  variants: props.data?.variants || "",
  prices: props.data?.prices || [],
  item_variants: props.data?.item_variants || [],
});

const setValidations = () => {
  setFieldRules("code", [required("Code is required")]);
  setFieldRules("name", [required("Name is required")]);
  setFieldRules("item_types_id",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [required("Item Type is required")]);
  setFieldRules("inventory_uom_id",  fields.value.tracked_by.value == "Expense"|| fields.value.tracked_by.value == "Service" ? [] : [
    required("Inventory Unit of Measure is required"),
  ]);
  setFieldRules("inventory_uom_qty",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [
    required("Inventory Measure Quantity is required"),
  ]);
  setFieldRules("purchase_uom_id",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [
    required("Purchase Unit of Measure is required"),
  ]);
  setFieldRules("purchase_uom_qty",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [
    required("Purchase Measure Quantity is required"),
  ]);
  setFieldRules("sales_uom_id",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [required("Sales Unit of Measure is required")]);
  setFieldRules("sales_uom_qty",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [
    required("Sales Measure Quantity is required"),
  ]);
  setFieldRules("tracked_by", [required("Item Tracking Type is required")]);
  setFieldRules("tax_details_id", [required("Tax Definition is required")]);
  setFieldRules("item_groups",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [required("Select at least 1 Item Group")]);
  setFieldRules("item_categories",  fields.value.tracked_by.value == "Expense" || fields.value.tracked_by.value == "Service" ? [] : [
    required("Select at least 1 Item Category required"),
  ]);
  setFieldRules("income_accounts_id", fields.value.tracked_by.value == "Expense" ? [] : [required("Income Account is required")]);
  setFieldRules("expense_accounts_id", fields.value.tracked_by.value == "Service" ? [] :  [required("Expense Account is required")]);

}

const tabList: Tabs[] = [];

const item_types = ref([]);
const unit_of_measures = ref([]);
const tax_details = ref([]);
const item_groups = ref([]);
const item_categories = ref([]);
const variants = ref([]);
const accounts = ref([]);
const outlets = ref([]);

const setLists = async (data: any) => {
  if (!data) return;
  item_types.value = data["item_types"];
  unit_of_measures.value = data["unit_of_measures"];
  tax_details.value = data["tax_details"];
  item_groups.value = data["item_groups"];
  item_categories.value = data["item_categories"];
  variants.value = data["variants"];
  accounts.value = data["accounts"];
  outlets.value = data["outlets"];
};

const handleTrackedBy = () => {
  if (fields.value.tracked_by.value == "Quantity")
    fields.value.is_expiry.value = false;
};

const handleItemTypes = (data: any) => {
  if (isEmpty(data) || props.mode != "create") return;
  fields.value.tax_details_id.value = data.tax_details_id;
  fields.value.income_accounts_id.value = data.income_accounts_id;
  fields.value.expense_accounts_id.value = data.expense_accounts_id;
  fields.value.inventory_uom_id.value = data.inventory_uom_id;
  fields.value.item_groups.value =
    data.item_groups?.split(",").reduce((a: number[], n: string) => {
      a.push(parseInt(n));
      return a;
    }, []) || "";
  fields.value.outlets.value =
    data.outlets?.split(",").reduce((a: number[], n: string) => {
      a.push(parseInt(n));
      return a;
    }, []) || "";
  fields.value.item_categories.value =
    data.item_categories?.split(",").reduce((a: number[], n: string) => {
      a.push(parseInt(n));
      return a;
    }, []) || "";
  fields.value.tracked_by.value = data.tracked_by;
  fields.value.allow_pre_order.value = data.allow_pre_order;
  fields.value.is_expiry.value = data.is_expiry;
  fields.value.has_variants.value = data.has_variants;
  fields.value.variants.value = data.variants;
  fields.value.inventory_uom_qty.value = data.inventory_uom_qty;
  fields.value.purchase_uom_id.value = data.purchase_uom_id;
  fields.value.purchase_uom_qty.value = data.purchase_uom_qty;
  fields.value.sales_uom_id.value = data.sales_uom_id;
  fields.value.sales_uom_qty.value = data.sales_uom_qty;
};

onMounted(() => {
  setLists(props.lists);
  handleGetRecord();
});
const handleSubmit = async (close: boolean, newRecord: boolean) => {
  setValidations();
  if (validateForm()) {
    const action = fields.value.uid.value != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setData(response.data);
      }
    }
  }
};

const packingInventoryFields = ref<TableField[]>([])
const packingInventoryData = ref<any[]>([])
const handleGetRecord = async () => {
  if (props.data) {
    const apiData = {
      id: props.data.uid,
    };
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "record",
      apiData
    );
    if (response.success == true) {
      setData(response.data);
      if (response.data.packing_prices && response.data.packing_prices.length > 0) {
        handlePackingInventory(response.data.packing_prices, response.data.inventory);
      }
    }
  }
};


const handlePackingInventory = (packingData: any[], inventoryData: any[]) => {
  packingInventoryFields.value = [];        
  packingInventoryFields.value.push({ name: 'warehouse', caption: 'Warehouse', sortable: false, fieldType: 'text' });
  packingData.forEach((price: any) => {
      const uom = packingInventoryFields.value.find((item: any) => item.caption == price.unit_of_measure);
      if (!uom) {
        packingInventoryFields.value.push({ name: price.code.toLowerCase(), caption: price.unit_of_measure, sortable: false, fieldType: 'number' });
      }
    });
    
  packingInventoryData.value = [];
  const reformattedData = inventoryData.map((inventory) => {
  const packing = packingData.find((p) => p.items_id === inventory.items_id); // Match packing data by items_id
  if (!packing) {
    return {
      warehouse: inventory.warehouse,
      [packing?.code.toLowerCase() || "0"]: "0",
    };
  }

  return {
    warehouse: inventory.warehouse,
    [packing.code.toLowerCase()]: (inventory.on_hand / packing.inventory_qty).toFixed(0), // Calculate and format value
  };
});

  packingInventoryData.value = reformattedData;
};
const setData = (data: any) => {
  setFormData(data);
  (fields.value.outlets.value =
    data.outlets?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || ""),
    (fields.value.tracked_by.value = data.tracked_by);
  (fields.value.optional_products.value =
    data.optional_products?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || ""),
    (fields.value.accessory_products.value =
      data.accessory_products?.split(",").reduce((a: any, n: any) => {
        a.push(parseInt(n));
        return a;
      }, []) || ""),
    (fields.value.alternative_products.value =
      data.alternative_products?.split(",").reduce((a: any, n: any) => {
        a.push(parseInt(n));
        return a;
      }, []) || ""),
    (fields.value.out_of_stock_message.value = data.out_of_stock_message);
  (fields.value.item_groups.value =
    data.item_groups?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || ""),
    (fields.value.item_categories.value =
      data.item_categories?.split(",").reduce((a: any, n: any) => {
        a.push(parseInt(n));
        return a;
      }, []) || ""),
    (fields.value.variants.value = parseInt(data.variants));

    prices.value = data.prices;
    quantity_prices.value = data.quantity_prices;
    packing_prices.value = data.packing_prices;
    inventory.value = data.inventory;
    barcodes.value = data.barcodes;
    photos.value = data.photos;
    batches.value = data.batches;
    serials.value = data.serials;
    variant_inventory.value = data.variant_inventory;
    variant_list.value = data.variant_list || [];

};

const prices = ref([]);
const quantity_prices = ref([]);
const packing_prices = ref([]);
const inventory = ref([]);
const barcodes = ref([]);
const photos = ref([]);
const batches = ref([]);
const serials = ref([]);
const variant_inventory = ref([]);
const variant_list = ref([]);

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})

const handlePrintLabel = async () => {
  const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "label",
      {
        barcode: formData.value.code,
        name: formData.value.name,
        price: formData.value.prices[0]?.selling_price,
        id: formData.value.uid,
      }
    );
    if (response.success == true) {
      const binaryData = atob(response.data);

      // Step 2: Create a Uint8Array to store the binary data
      const len = binaryData.length;
      const uint8Array = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
      }

      // Step 3: Create a Blob from the binary data
      const blob = new Blob([uint8Array], { type: "application/pdf" });

      // Step 4: Create a Blob URL
      const blobURL = URL.createObjectURL(blob);

      let pdfFrame = document.getElementById("pdf-printer") as HTMLIFrameElement;
    if (pdfFrame) {
      pdfFrame.src = blobURL;
      pdfFrame.onload = () => {
        pdfFrame?.contentWindow?.focus();
        pdfFrame?.contentWindow?.print();
      };
    }
    }
}

</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :listCreateName="listCreateName || listCreateCode"
    :perm="permissions"
    :tabList="tabList"
    :formData="formData"
    :ignorePermission="ignorePermission"
    @save="handleSubmit"
    width="50rem"
    @close="$emit('close')"
  >
  <template #header>
    <Buttons
      icon="Printer"
      v-if="mode == 'view' || mode == 'edit'"
      caption="Print Label"
      @click="handlePrintLabel"
       />
    </template>
    <template #form>
      <div class="grid md:grid-cols-1 gap-6">
      <div id="document-section">
        <FileUpload
          label="Photo"
          accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
          :max-size="5"
          :multiple="false"
          :readonly="isReadOnly"
          :image="true"
          path="items"
          v-model="fields.photo.value"
        />
      </div>
      <div class="md:flex gap-6">
        <TextInput
          v-model="fields.code.value"
          label="Code"
          class="md:w-1/3"
          placeholder="Enter Code"
          :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true"
        />

        <TextInput
          v-model="fields.name.value"
          label="Name"
          class="md:w-2/3"
          placeholder="Enter Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />
        <SearchSelect
          v-model="fields.tracked_by.value"
          label="Tracked By"
          :options="trackedByList"
          :readonly="isReadOnly"
          :error="fields.tracked_by.touched ? fields.tracked_by.error : null"
          @blur="fields.tracked_by.touched = true"
          @select="handleTrackedBy"
        />
      </div>
      <div class="md:flex gap-6">
        <SearchSelect
          v-model="fields.item_types_id.value"
          end-point="inventory/item-management/item-types"
          label="Item Type"
          class="md:w-1/3"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :allow-new-record="true"
          :readonly="isReadOnly"
          :error="
            fields.item_types_id.touched ? fields.item_types_id.error : null
          "
          :options="item_types"
          @blur="fields.item_types_id.touched = true"
          @select="handleItemTypes"
        />

        <TextInput
          v-model="fields.sales_name.value"
          class="md:w-2/3"
          label="Sales Name"
          :readonly="isReadOnly"
          :error="fields.sales_name.touched ? fields.sales_name.error : null"
          @blur="fields.sales_name.touched = true"
        />
      </div>
      <div id="" class="grid md:grid-cols-3 gap-6">
        

        <SearchSelect
          v-model="fields.inventory_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          label="Inventory Unit of Measure"
          local-storage="unit_of_measure"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          :options="unit_of_measures"
          :error="
            fields.inventory_uom_id.touched
              ? fields.inventory_uom_id.error
              : null
          "
          @blur="fields.inventory_uom_id.touched = true"
        />

        <AmountInput
          v-model="fields.inventory_uom_qty.value"
          label="Inventory UOM Qty"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          width="150px"
          :error="
            fields.inventory_uom_qty.touched
              ? fields.inventory_uom_qty.error
              : null
          "
          @blur="fields.inventory_uom_qty.touched = true"
        />

        <SearchSelect
          v-model="fields.tax_details_id.value"
          end-point="finance/master-data/tax-definitions"
          label="Tax Definition"
          :readonly="isReadOnly"
          :options="tax_details"
          :error="
            fields.tax_details_id.touched ? fields.tax_details_id.error : null
          "
          @blur="fields.tax_details_id.touched = true"
        />

        <SearchSelect
          v-model="fields.purchase_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          local-storage="unit_of_measure"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          label="Purchase Unit of Measure"
          :readonly="isReadOnly"
          :options="unit_of_measures"
          :error="
            fields.purchase_uom_id.touched ? fields.purchase_uom_id.error : null
          "
          @blur="fields.purchase_uom_id.touched = true"
        />

        <AmountInput
          v-model="fields.purchase_uom_qty.value"
          label="Purchase UOM Qty"
          width="150px"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          :error="
            fields.purchase_uom_qty.touched
              ? fields.purchase_uom_qty.error
              : null
          "
          @blur="fields.purchase_uom_qty.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.item_groups.value"
          end-point="inventory/item-management/item-groups"
          label="Item Groups"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Expense'"
          :options="item_groups"
          :allow-new-record="true"
          :error="fields.item_groups.touched ? fields.item_groups.error : null"
          @blur="fields.item_groups.touched = true"
        />

        <SearchSelect
          v-model="fields.sales_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          label="Sales Unit of Measure"
          local-storage="unit_of_measure"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          :options="unit_of_measures"
          :error="
            fields.sales_uom_id.touched ? fields.sales_uom_id.error : null
          "
          @blur="fields.sales_uom_id.touched = true"
        />

        <AmountInput
          v-model="fields.sales_uom_qty.value"
          label="Sales UOM Qty"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          width="150px"
          :readonly="isReadOnly"
          :error="
            fields.sales_uom_qty.touched ? fields.sales_uom_qty.error : null
          "
          @blur="fields.sales_uom_qty.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.item_categories.value"
          end-point="inventory/item-management/item-categories"
          label="Item Categories"
          v-if="fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          :allow-new-record="true"
          :options="item_categories"
          :error="
            fields.item_categories.touched ? fields.item_categories.error : null
          "
          @blur="fields.item_categories.touched = true"
        />

        <SearchSelect
          v-model="fields.brands_id.value"
          end-point="inventory/item-management/brands"
          label="Brand"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          :error="
            fields.brands_id.touched ? fields.brands_id.error : null
          "
          @blur="fields.brands_id.touched = true"
        />

        <AmountInput
          v-model="fields.weight.value"
          label="Weight"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          width="150px"
          :error="fields.weight.touched ? fields.weight.error : null"
          @blur="fields.weight.touched = true"
        />

        <AmountInput
          v-model="fields.volume.value"
          label="Volume"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          width="150px"
          :readonly="isReadOnly"
          :error="fields.volume.touched ? fields.volume.error : null"
          @blur="fields.volume.touched = true"
        />

        <CheckInput
          v-model="fields.is_package.value"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          label="Packaged Item"
          :error="fields.is_package.touched ? fields.is_package.error : null"
          @blur="fields.is_package.touched = true"
        />

        <CheckInput
          v-model="fields.use_package_price.value"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          label="Use Package Price"
          :error="
            fields.use_package_price.touched
              ? fields.use_package_price.error
              : null
          "
          @blur="fields.use_package_price.touched = true"
        />

        <CheckInput
          v-model="fields.is_expiry.value"
          :readonly="isReadOnly || fields.tracked_by.value == 'Quantity'"
          label="Track Expiry"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense' && fields.tracked_by.value != 'Quantity'"
          :error="fields.is_expiry.touched ? fields.is_expiry.error : null"
          @blur="fields.is_expiry.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.outlets.value"
          label="Restricted to Outlets"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Expense'"
          :options="outlets"
          :error="fields.outlets.touched ? fields.outlets.error : null"
          @blur="fields.outlets.touched = true"
        />

        <CheckInput
          v-model="fields.has_variants.value"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          label="Has Variants"
          :error="
            fields.has_variants.touched ? fields.has_variants.error : null
          "
          @blur="fields.has_variants.touched = true"
        />

        <SearchSelect
          v-model="fields.variants.value"
          label="Variants"
          v-if="fields.has_variants.value == true"
          end-point="inventory/item-management/variants"
          :readonly="isReadOnly"
          :options="variants"
          :allow-new-record="true"
          :error="fields.variants.touched ? fields.variants.error : null"
          :disabled="!formData.has_variants"
          @blur="fields.variants.touched = true"
        />

        <CheckInput
          v-model="fields.to_weigh.value"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          label="To Weigh (Scale)"
          :error="fields.to_weigh.touched ? fields.to_weigh.error : null"
          @blur="fields.to_weigh.touched = true"
        />

        <CheckInput
          v-model="fields.allow_pre_order.value"
          :readonly="isReadOnly"
          label="Allow Pre Order"
          v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
          :error="
            fields.allow_pre_order.touched ? fields.allow_pre_order.error : null
          "
          @blur="fields.allow_pre_order.touched = true"
        />
        <CheckInput
          v-model="fields.flexi_price.value"
          :readonly="isReadOnly"
          label="Allow Flexible Price"
          :error="
            fields.flexi_price.touched ? fields.flexi_price.error : null
          "
          @blur="fields.flexi_price.touched = true"
        />
        <CheckInput
          v-model="fields.ignore_discount.value"
          :readonly="isReadOnly"
          label="Ignore Discount"
          :error="
            fields.ignore_discount.touched ? fields.ignore_discount.error : null
          "
          @blur="fields.ignore_discount.touched = true"
        />
      </div>
      <div class="md:flex gap-6">
        <TextInput
          v-model="fields.internal_notes.value"
          class="w-2/3"
          label="Internal Notes"
          type="textarea"
          :readonly="isReadOnly"
          :error="
            fields.internal_notes.touched ? fields.internal_notes.error : null
          "
          @blur="fields.internal_notes.touched = true"
        />

        <TextInput
          v-model="fields.out_of_stock_message.value"
          class="w-1/3"
          label="Out of Stock Message"
          :readonly="isReadOnly"
          :error="
            fields.out_of_stock_message.touched
              ? fields.out_of_stock_message.error
              : null
          "
          @blur="fields.out_of_stock_message.touched = true"
        />
      </div>
      <div id="" class="grid md:grid-cols-3 gap-6">
        <SearchSelectMultiItems
          v-model="fields.optional_products.value"
          end-point="inventory/item-management/items"
          label="Optional Products"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Expense' && fields.tracked_by.value != 'Service'"
          :remote="true"
          :error="
            fields.optional_products.touched
              ? fields.optional_products.error
              : null
          "
          @blur="fields.optional_products.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.accessory_products.value"
          end-point="inventory/item-management/items"
          label="Accessory Products"
          v-if="fields.tracked_by.value != 'Expense' && fields.tracked_by.value != 'Service'"
          :readonly="isReadOnly"
          :remote="true"
          :error="
            fields.accessory_products.touched
              ? fields.accessory_products.error
              : null
          "
          @blur="fields.accessory_products.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.alternative_products.value"
          end-point="inventory/item-management/items"
          label="Alternative Products"
          :readonly="isReadOnly"
          v-if="fields.tracked_by.value != 'Expense' && fields.tracked_by.value != 'Service'"
          :remote="true"
          :error="
            fields.alternative_products.touched
              ? fields.alternative_products.error
              : null
          "
          @blur="fields.alternative_products.touched = true"
        />

        <SearchSelect
          v-model="fields.income_accounts_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Income Account"
          local-storage="accounts"
          v-if="fields.tracked_by.value != 'Expense'"
          :readonly="isReadOnly"
          :options="accounts"
          :error="
            fields.income_accounts_id.touched
              ? fields.income_accounts_id.error
              : null
          "
          @blur="fields.income_accounts_id.touched = true"
        />

        <SearchSelect
          v-model="fields.expense_accounts_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Expense Account"
          v-if="fields.tracked_by.value != 'Service'"
          local-storage="accounts"
          :readonly="isReadOnly"
          :options="accounts"
          :error="
            fields.expense_accounts_id.touched
              ? fields.expense_accounts_id.error
              : null
          "
          @blur="fields.expense_accounts_id.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>

      <ItemPricesListView
        :data="prices"
        v-if="fields.tracked_by.value != 'Expense' && !fields.has_variants.value"
        :permissions="permissions"
        :item="formData"
      />

      <ItemVariantsList v-else-if="fields.has_variants.value" :item="formData" :data="fields.item_variants.value" :permissions="permissions" />

      <ItemPackingPricesListView
        :data="packing_prices"
        :permissions="permissions"
        v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
        :item="formData"
        :unit_of_measures="unit_of_measures"
      />
      <ItemQuantityPricesListView
        :data="quantity_prices"
        :permissions="permissions"
        v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
        :item="formData"
      />

      <template v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'">
      <InventoryListView
        :data="inventory"
        v-if="!fields.has_variants.value"
        :unit_of_measures="unit_of_measures"
        :packingInventoryFields="packingInventoryFields"
        :packingInventoryData="packingInventoryData"
        :permissions="permissions"
        :item="formData"
      />

      <ItemVariantInventory v-else :data="variant_inventory" :item="formData" v-if="fields.has_variants.value && variant_inventory" />

    </template>

      <GridInput label="Variants" v-if="fields.has_variants.value && variant_list && fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'">
        <VariantList v-for="variant in variant_list" :item="formData" :data="variant" :permissions="permissions" :variant="variant"/>
      </GridInput>
      
      
      <BatchListView
        v-if="batches.length > 0"
        :data="batches"
        :permissions="permissions"
        :item="formData"
      />
      <SerialListView
        v-if="serials.length > 0"
        :data="serials"
        :ignorePermissions="true"
        :permissions="permissions"
        :item="formData"
      />
      <BarodesListView
        :data="barcodes"
        v-if="fields.tracked_by.value != 'Service' && fields.tracked_by.value != 'Expense'"
        :permissions="permissions"
        :item="formData"
      />
      <PhotosListView
        :data="photos"
        v-if="fields.tracked_by.value != 'Expense'"
        :permissions="permissions"
        :item="formData"
      />

      <div class="">
        <TextAreaInput
          v-model="fields.short_description.value"
          label="Short Description"
          :rows="5"
          type="textarea"
          :readonly="isReadOnly"
          :error="
            fields.short_description.touched
              ? fields.short_description.error
              : null
          "
          @blur="fields.short_description.touched = true"
        />
      </div>
      <div class="">
        <TextAreaInput
          v-model="fields.description.value"
          label="Description"
          :rows="10"
          type="textarea"
          :readonly="isReadOnly"
          :error="fields.description.touched ? fields.description.error : null"
          @blur="fields.description.touched = true"
        />
      </div>
      </div>
    </template>
  </ModalFormTemplate>
</template>
